import React, { useState, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Button, Badge, Spinner, Modal, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faShieldAlt, faCheck, faSearch,
  faChevronDown, faTrash, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { createRole, addCapabilitiesToRole, getRoleCapabilities, deleteRole } from '../services/authorizationService';

// ─── Create Role Modal ────────────────────────────────────────────────────────
const CreateRoleModal = forwardRef(({ onCreated }, ref) => {
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useImperativeHandle(ref, () => ({
    show: () => { setName(''); setDescription(''); setError(null); setShow(true); }
  }));

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createRole({ name: name.trim(), description: description.trim() });
      setShow(false);
      onCreated?.();
    } catch (e) {
      setError(e?.message || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} className="auth-modal">
      <Modal.Header closeButton>
        <Modal.Title>Create New Role</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Role Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Project Manager"
            value={name}
            onChange={e => setName(e.target.value)}
            className="auth-input"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Describe what this role can do..."
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="auth-input"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setShow(false)}>Cancel</Button>
        <Button className="auth-btn-primary" onClick={handleSubmit} disabled={saving || !name.trim()}>
          {saving && <Spinner size="sm" className="me-2" />}
          Create Role
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteRoleModal = forwardRef(({ onDeleted }, ref) => {
  const [show, setShow] = useState(false);
  const [role, setRole] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useImperativeHandle(ref, () => ({
    show: (r) => { setRole(r); setShow(true); }
  }));

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // apiCall handles the error sweetAlert automatically on 400 (user assigned)
      const res = await deleteRole(role.id);
      if (res?.success || res?.isSuccess !== false) {
        setShow(false);
        onDeleted?.();
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} className="auth-modal" centered>
      <Modal.Header closeButton>
        <Modal.Title>Delete Role</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="delete-confirm-body">
          <div className="delete-confirm-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <p>
            Are you sure you want to delete <strong>{role?.name}</strong>?
          </p>
          <p className="text-muted small mb-0">
            This will also remove all capability assignments for this role.
            If users are assigned this role, you must remove those assignments first.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setShow(false)}>Cancel</Button>
        <Button className="auth-btn-danger" onClick={handleDelete} disabled={deleting}>
          {deleting && <Spinner size="sm" className="me-2" />}
          Delete Role
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

// ─── Grouping helper ──────────────────────────────────────────────────────────
const groupByModuleAndResource = (capabilities) => {
  const groups = {};
  capabilities.forEach(cap => {
    const mod = cap.moduleKey || 'General';
    const rt = cap.targetResourceType || 'Other';
    if (!groups[mod]) groups[mod] = {};
    if (!groups[mod][rt]) groups[mod][rt] = [];
    groups[mod][rt].push(cap);
  });
  return groups;
};

// ─── Collapsible RT group (select / deselect all) ────────────────────────────
const RtCapGroup = ({ rt, caps, assigned, onToggle, onSelectAll, onDeselectAll }) => {
  const [collapsed, setCollapsed] = useState(true);
  const ids = caps.map(c => String(c.id));
  const selectedCount = ids.filter(id => assigned.includes(id)).length;
  const allSelected = selectedCount === ids.length;

  return (
    <div className="role-modal-rt-group">
      <div className="role-modal-rt-header" onClick={() => setCollapsed(p => !p)}>
        <div className="rt-header-left">
          <FontAwesomeIcon icon={faChevronDown} className={`rt-collapse-icon ${collapsed ? 'collapsed' : ''}`} />
          <span className="rt-header-name">{rt}</span>
          <Badge className="rt-count-badge">{selectedCount}/{caps.length}</Badge>
        </div>
        <div className="rt-header-actions" onClick={e => e.stopPropagation()}>
          <button
            className={`rt-bulk-btn ${allSelected ? 'active' : ''}`}
            onClick={() => allSelected ? onDeselectAll(ids) : onSelectAll(ids)}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="caps-check-grid">
          {caps.map(cap => {
            const isAssigned = assigned.includes(String(cap.id));
            return (
              <div
                key={cap.id}
                className={`cap-check-item ${isAssigned ? 'selected' : ''}`}
                onClick={() => onToggle(String(cap.id))}
              >
                <div className={`cap-check-box ${isAssigned ? 'checked' : ''}`}>
                  {isAssigned && <FontAwesomeIcon icon={faCheck} />}
                </div>
                <div className="cap-check-info">
                  <strong>{cap.name}</strong>
                  {cap.description && <p className="mb-0 small text-muted">{cap.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Collapsible Module group ─────────────────────────────────────────────────
const ModuleGroup = ({ mod, resourceTypes, assigned, onToggle, onSelectAll, onDeselectAll }) => {
  const [collapsed, setCollapsed] = useState(true);
  const allCaps = Object.values(resourceTypes).flat();
  const selectedCount = allCaps.filter(c => assigned.includes(String(c.id))).length;

  return (
    <div className="module-group">
      <div className="module-group-header" onClick={() => setCollapsed(p => !p)}>
        <div className="module-header-left">
          <FontAwesomeIcon icon={faChevronDown} className={`rt-collapse-icon ${collapsed ? 'collapsed' : ''}`} />
          <span className="module-header-name">{mod}</span>
          <Badge className="auth-badge-role module-count-badge">
            {selectedCount}/{allCaps.length}
          </Badge>
        </div>
      </div>
      {!collapsed && (
        <div className="module-group-body">
          {Object.entries(resourceTypes).sort(([a], [b]) => a.localeCompare(b)).map(([rt, caps]) => (
            <RtCapGroup
              key={rt}
              rt={rt}
              caps={caps}
              assigned={assigned}
              onToggle={onToggle}
              onSelectAll={onSelectAll}
              onDeselectAll={onDeselectAll}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Edit Role Modal ──────────────────────────────────────────────────────────
const EditRoleModal = forwardRef(({ allCapabilities, onSaved }, ref) => {
  const [show, setShow] = useState(false);
  const [role, setRole] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useImperativeHandle(ref, () => ({
    show: async (r) => {
      setRole(r);
      setError(null);
      setSearch('');
      setShow(true);
      setLoading(true);
      try {
        const caps = await getRoleCapabilities(r.id);
        setAssigned(caps.map(c => String(c.id || c.capabilityId)));
      } catch (e) {
        setError('Failed to load capabilities');
      } finally {
        setLoading(false);
      }
    }
  }));

  const toggleCap = (capId) =>
    setAssigned(prev => prev.includes(capId) ? prev.filter(id => id !== capId) : [...prev, capId]);

  const selectAll = (ids) => setAssigned(prev => [...new Set([...prev, ...ids])]);
  const deselectAll = (ids) => setAssigned(prev => prev.filter(id => !ids.includes(id)));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await addCapabilitiesToRole({ roleId: role.id, capabilityIds: assigned.map(Number) });
      setShow(false);
      onSaved?.();
    } catch (e) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    const filtered = allCapabilities.filter(c =>
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.targetResourceType || '').toLowerCase().includes(search.toLowerCase())
    );
    return groupByModuleAndResource(filtered);
  }, [allCapabilities, search]);

  const moduleKeys = Object.keys(grouped).sort();

  return (
    <Modal show={show} onHide={() => setShow(false)} size="lg" className="auth-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit Role: {role?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="auth-search-bar mb-3">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <Form.Control
            type="text"
            placeholder="Search capabilities or resource type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="auth-search-input"
          />
        </div>
        <div className="edit-role-summary mb-3">
          <Badge className="auth-badge-cap">{assigned.length} capabilities selected</Badge>
        </div>
        {loading ? (
          <div className="auth-loading"><Spinner size="sm" className="me-2" /> Loading...</div>
        ) : (
          <div className="module-groups-list">
            {moduleKeys.map(mod => (
              <ModuleGroup
                key={mod}
                mod={mod}
                resourceTypes={grouped[mod]}
                assigned={assigned}
                onToggle={toggleCap}
                onSelectAll={selectAll}
                onDeselectAll={deselectAll}
              />
            ))}
            {moduleKeys.length === 0 && (
              <p className="text-muted text-center py-4">No capabilities match your search.</p>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => setShow(false)}>Cancel</Button>
        <Button className="auth-btn-primary" onClick={handleSave} disabled={saving}>
          {saving && <Spinner size="sm" className="me-2" />}
          Save Capabilities
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

// ─── Main Roles List ──────────────────────────────────────────────────────────
const RolesList = ({ roles, allCapabilities, isLoading, onRefetch }) => {
  const createModalRef = useRef(null);
  const editModalRef = useRef(null);
  const deleteModalRef = useRef(null);
  const [search, setSearch] = useState('');

  const filteredRoles = useMemo(() => {
    const s = search.toLowerCase();
    if (!s) return roles;
    return roles.filter(r =>
      r.name.toLowerCase().includes(s) ||
      (r.description || '').toLowerCase().includes(s)
    );
  }, [roles, search]);

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <p>Loading roles...</p>
      </div>
    );
  }

  return (
    <div className="roles-list-tab">
      <div className="auth-tab-header">
        <div>
          <h4>Roles</h4>
          <p className="text-muted mb-0">{roles.length} total roles</p>
        </div>
        <Button className="auth-btn-primary" onClick={() => createModalRef.current?.show()}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Create Role
        </Button>
      </div>

      <div className="auth-search-bar mb-4">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <Form.Control
          type="text"
          placeholder="Search roles by name or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="auth-search-input"
        />
      </div>

      <div className="roles-list">
        {filteredRoles.map(role => (
          <div key={role.id} className="role-list-item">
            <div className="role-list-icon">
              <FontAwesomeIcon icon={faShieldAlt} />
            </div>
            <div className="role-list-info">
              <strong className="role-list-name">{role.name}</strong>
              {role.description && <span className="role-list-desc">{role.description}</span>}
            </div>
            <div className="role-list-meta">
              <span className="text-muted small">
                {role.capabilityCount != null ? `${role.capabilityCount} capabilities` : ''}
              </span>
            </div>
            <div className="role-list-actions">
              <Button
                className="auth-btn-edit"
                onClick={() => editModalRef.current?.show(role)}
              >
                <FontAwesomeIcon icon={faEdit} className="me-1" />
                Edit
              </Button>
              <Button
                className="auth-btn-delete"
                onClick={() => deleteModalRef.current?.show(role)}
                title="Delete role"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
          </div>
        ))}

        {filteredRoles.length === 0 && (
          <div className="auth-empty">
            <FontAwesomeIcon icon={faShieldAlt} className="auth-empty-icon" />
            <p>{roles.length === 0 ? 'No roles yet. Create your first role.' : 'No roles match your search.'}</p>
          </div>
        )}
      </div>

      <CreateRoleModal ref={createModalRef} onCreated={onRefetch} />
      <EditRoleModal ref={editModalRef} allCapabilities={allCapabilities} onSaved={onRefetch} />
      <DeleteRoleModal ref={deleteModalRef} onDeleted={onRefetch} />
    </div>
  );
};

export default RolesList;